import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Upload, Loader2, CircleDot } from "lucide-react";

const EMPTY = {
  name: "", description: "", image_url: "",
  price: 15, stock: 50, enabled: true,
};

export default function AdminAddOns() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [priceDraft, setPriceDraft] = useState({});
  const [savingPrice, setSavingPrice] = useState(null);
  const [toggling, setToggling] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    const r = await api.get("/admin/addons");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      name: a.name,
      description: a.description || "",
      image_url: a.image_url || "",
      price: a.price,
      stock: a.stock,
      enabled: !!a.enabled,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
      };
      if (editing) await api.put(`/admin/addons/${editing}`, payload);
      else await api.post("/admin/addons", payload);
      toast.success(editing ? "Add-on updated" : "Add-on added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this add-on?")) return;
    await api.delete(`/admin/addons/${id}`);
    toast.success("Add-on removed");
    load();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const savePrice = async (id) => {
    const n = parseFloat(priceDraft[id]);
    if (Number.isNaN(n) || n < 0) { toast.error("Invalid price"); return; }
    setSavingPrice(id);
    try {
      const r = await api.put(`/admin/addons/${id}`, { price: n });
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, price: r.data.price } : it));
      setPriceDraft((d) => { const c = { ...d }; delete c[id]; return c; });
      toast.success(`Price → £${n.toFixed(2)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSavingPrice(null); }
  };

  const toggleEnabled = async (a) => {
    setToggling(a.id);
    try {
      const r = await api.put(`/admin/addons/${a.id}`, { enabled: !a.enabled });
      setItems((prev) => prev.map((it) => it.id === a.id ? { ...it, enabled: r.data.enabled } : it));
      toast.success(r.data.enabled ? "Add-on live" : "Add-on hidden");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setToggling(null); }
  };

  return (
    <div data-testid="admin-addons">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase">Boardroom</div>
          <h1 className="font-display text-4xl text-[#F2EBDD] mt-1">Add-Ons</h1>
          <p className="text-[#A8A39D] mt-2 text-sm max-w-xl">
            Extras customers can bolt onto a jersey order (matchballs, scarves, boots…). Toggle Live / Hidden anytime.
          </p>
        </div>
        <button data-testid="new-addon-btn" onClick={openNew} className="btn-primary">
          <Plus size={16} /> Add Extra
        </button>
      </div>

      <div className="mt-8 border border-[#332D2B] bg-[#1C1816] overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-[#332D2B]">
            <tr className="text-left font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">
              <th className="p-4">Image</th>
              <th className="p-4">Name</th>
              <th className="p-4">Price</th>
              <th className="p-4">Stock</th>
              <th className="p-4">Live</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => {
              const draft = priceDraft[a.id];
              const dirty = draft !== undefined && parseFloat(draft) !== Number(a.price);
              return (
                <tr key={a.id} className="border-b border-[#332D2B] hover:bg-[#2A2320]">
                  <td className="p-4">
                    {a.image_url ? (
                      <img src={a.image_url} alt="" className="w-12 h-12 object-cover border border-[#332D2B]" />
                    ) : (
                      <div className="w-12 h-12 border border-dashed border-[#332D2B] flex items-center justify-center text-[#756F6B]">
                        <CircleDot size={16} />
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-[#F2EBDD]">
                    <div>{a.name}</div>
                    <div className="text-[11px] text-[#756F6B] line-clamp-1 max-w-xs">{a.description}</div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="text-[#C8A96A] font-mono-retro text-xs">£</span>
                      <input
                        data-testid={`addon-price-${a.id}`}
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-20 bg-[#0F0C0B] border border-[#332D2B] focus:border-[#C8A96A] outline-none text-[#F2EBDD] text-sm px-2 py-1"
                        value={draft !== undefined ? draft : a.price}
                        onChange={(e) => setPriceDraft((d) => ({ ...d, [a.id]: e.target.value }))}
                      />
                      {dirty && (
                        <button
                          data-testid={`save-addon-price-${a.id}`}
                          onClick={() => savePrice(a.id)}
                          disabled={savingPrice === a.id}
                          className="text-[#C8A96A] hover:text-[#F2EBDD] font-mono-retro text-[10px] tracking-[0.2em] uppercase disabled:opacity-40"
                        >
                          {savingPrice === a.id ? "…" : "Save"}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-[#A8A39D]">{a.stock}</td>
                  <td className="p-4">
                    <button
                      data-testid={`toggle-addon-${a.id}`}
                      onClick={() => toggleEnabled(a)}
                      disabled={toggling === a.id}
                      className={`font-mono-retro text-[10px] tracking-[0.2em] uppercase px-2 py-1 border transition-colors ${
                        a.enabled
                          ? "border-[#7FB77E] text-[#7FB77E]"
                          : "border-[#8B4530] text-[#D4936C]"
                      } disabled:opacity-40`}
                    >
                      {a.enabled ? "● Live" : "○ Hidden"}
                    </button>
                  </td>
                  <td className="p-4 text-right space-x-2 whitespace-nowrap">
                    <button data-testid={`edit-addon-${a.id}`} onClick={() => openEdit(a)} className="text-[#A8A39D] hover:text-[#C8A96A] inline-flex items-center gap-1 text-sm"><Edit3 size={14} /> Edit</button>
                    <button data-testid={`delete-addon-${a.id}`} onClick={() => remove(a.id)} className="text-[#A8A39D] hover:text-[#8B4530] inline-flex items-center gap-1 text-sm"><Trash2 size={14} /> Delete</button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-[#A8A39D]">No add-ons yet — the Jabulani will seed on first backend start.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#0F0C0B]/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="border-2 border-[#332D2B] bg-[#1C1816] max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#332D2B]">
              <div className="font-display text-2xl text-[#F2EBDD]">{editing ? "Edit Add-On" : "New Add-On"}</div>
              <button onClick={() => setOpen(false)} className="text-[#A8A39D] hover:text-[#F2EBDD]"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="fz-label">Name</label>
                <input data-testid="addon-form-name" required className="fz-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="fz-label">Description</label>
                <textarea data-testid="addon-form-description" rows={3} className="fz-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="fz-label">Image</label>
                <div className="flex gap-2">
                  <input
                    data-testid="addon-form-image"
                    className="fz-input flex-1"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="Paste URL or upload →"
                  />
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
                  <button
                    type="button"
                    data-testid="addon-form-upload"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-secondary text-xs"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                  </button>
                </div>
                {form.image_url && (
                  <div className="mt-3 w-32 h-32 border border-[#332D2B] bg-[#2A2320] overflow-hidden">
                    <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="fz-label">Price (£)</label>
                  <input data-testid="addon-form-price" type="number" step="0.01" required className="fz-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Stock</label>
                  <input data-testid="addon-form-stock" type="number" required className="fz-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-[#F2EBDD]">
                <input data-testid="addon-form-enabled" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-[#C8A96A]" />
                <span className="font-mono-retro text-xs tracking-[0.2em] uppercase">Live (customers can add to cart)</span>
              </label>
              <div className="flex gap-3 pt-3">
                <button data-testid="addon-form-save" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Save Add-On"}</button>
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
